import type { SchoolRecord } from '@/models';
import { get, post, withoutActiveRole } from '@/lib/apiClient';
import {
  getDefaultRole,
  PORTAL_HOME_PATH,
  portalOfPath,
  portalOfShell,
  portalsOfPaths,
  type Portal,
  type RoleCode,
} from '@/routes/roles';
import { menuPaths, type FabricaMenus } from './fabricaMenus';

/**
 * Sign-in from Fabrica's users. Azure AD B2C (email, password and MFA) gives a token
 * (see b2c.ts, filled in by the team); POST /auth/User/ValidateActiveUser checks the
 * person is in Camptain's users table and returns who they are, their schools and roles;
 * GET /api/me adds each role's icon, web home and operations. The app keeps both in global
 * state and sends the same B2C token on every call; Camptain issues no token of its own.
 */

/** A role's home menu in one app (GET /api/me): the menu, its path and the shell it opens in (e.g. `/principal`). */
export interface RoleHome {
  menuId: number;
  path: string;
  shell: string;
}

export interface AccessRole {
  roleId: number;
  code: string;
  /** Fabrica's display name (Roles.DisplayName). */
  name: string;
  /** Fabrica's icon for the role, an MUI icon name (e.g. `LocalLibraryOutlined`); from /api/me. */
  icon?: string | null;
  /**
   * The role's first web menu with a path, from /api/me. Null both when the role has no web menu and
   * when the API could not read Fabrica, so it only names the home: whether the role opens on the web
   * is its menus' call (see `webRolesOf`). Undefined when /api/me did not answer.
   */
  webHome?: RoleHome | null;
  /**
   * Fabrica SystemOperation keys granted to the role (e.g. `Staff.Sensitive.View`): from /api/me for
   * school roles, from ValidateActiveUser for platform roles. Null or undefined when not known.
   */
  operations?: string[] | null;
}

/** A role as GET /api/me returns it. */
export interface MeRole {
  roleId: number;
  code: string;
  name: string;
  icon: string | null;
  webHome: RoleHome | null;
  mobileHome?: RoleHome | null;
  operations: string[];
}

export interface AccessOrganization {
  organizationId: number;
  organizationCode: string;
  organizationName: string;
  city?: string | null;
  logoPath?: string | null;
  roles: AccessRole[];
}

/** The answer to POST /auth/User/ValidateActiveUser, kept in global state. */
export interface UserAccess {
  user: {
    userId: number;
    firstName: string;
    lastName: string;
    emailAddress?: string | null;
    phoneNumber?: string | null;
  };
  organizations: AccessOrganization[];
  activeOrganization: AccessOrganization;
  branchId?: number | null;
  authorizedBranchIds?: number[];
  roles: AccessRole[];
  /** Fabrica platform roles held in any workspace (no operations: /api/me lists school roles only). */
  platformRoles?: AccessRole[] | null;
}

/** GET /api/me: the person's roles (icon, web home, operations), own student record, children and staff record. */
export interface MeLinks {
  roles?: MeRole[];
  studentId?: number | null;
  children?: { studentId: number }[];
  staffId?: number | null;
}

const bearer = (token: string, organizationId?: number, branchId?: number | null) => ({
  headers: {
    Authorization: `Bearer ${token}`,
    ...(organizationId ? { 'X-Organization-Id': String(organizationId) } : {}),
    ...(branchId ? { 'X-Branch-Id': String(branchId) } : {}),
  },
});

/** Is this B2C sign-in a Camptain user? Throws (403, code "not_registered") when not. */
export function validateActiveUser(
  token: string,
  organizationId?: number,
  branchId?: number,
): Promise<UserAccess> {
  return post<UserAccess>(
    '/auth/User/ValidateActiveUser',
    { organizationId, branchId },
    withoutActiveRole(bearer(token, organizationId, branchId)),
  );
}

/** GET /api/me with every held role: the active-role header would narrow its roles to the chosen one. */
export function fetchMeLinks(
  token: string,
  organizationId: number,
  branchId?: number | null,
): Promise<MeLinks> {
  return get<MeLinks>('/api/me', withoutActiveRole(bearer(token, organizationId, branchId)));
}

export interface BranchOption {
  organizationId: number;
  branchId: number;
  branchName: string;
  school: SchoolRecord | null;
}

export function fetchAuthorizedBranches(
  token: string,
  organizationId: number,
  branchId?: number | null,
): Promise<BranchOption[]> {
  return get<BranchOption[]>(
    '/api/me/branches',
    withoutActiveRole(bearer(token, organizationId, branchId)),
  );
}

export async function fetchAuthorizedSchools(
  token: string,
  organizationId: number,
  branchId?: number | null,
): Promise<SchoolRecord[]> {
  return (await fetchAuthorizedBranches(token, organizationId, branchId)).flatMap((b) =>
    b.school ? [b.school] : [],
  );
}

/** `roles` with what GET /api/me says of each (icon, web home, operations); unchanged without it. */
export function withMeRoles(roles: AccessRole[], me: MeRole[] | undefined): AccessRole[] {
  if (!me) return roles;
  return roles.map((r) => {
    const m = me.find((x) => x.roleId === r.roleId);
    return m ? { ...r, icon: m.icon, webHome: m.webHome, operations: m.operations } : r;
  });
}

/** One of the person's roles that opens on the web: its portal and home from Fabrica. */
export interface WebRole {
  roleId: number;
  code: RoleCode;
  /** The API's display name (Roles.DisplayName). */
  name: string;
  /** The API's icon name, or null. */
  icon: string | null;
  /** Its base portal: the shell it opens in. */
  portal: Portal;
  /** Every portal its menus reach, base portal first: their routes are mounted. */
  portals: Portal[];
  /** The page it opens on: its web home. */
  home: string;
  /** Its granted Fabrica operations; null when /api/me did not say. */
  operations: string[] | null;
}

/**
 * The person's roles that open on the web, in the order they came back. Fabrica's web menus decide
 * whether a role opens on the web: once they are loaded, a role with neither a web home nor a menu
 * path in a portal is dropped (it uses the mobile app). Its portal and home are its web home from
 * /api/me, else its first web menu with a path. Without menus, a role with a web home opens there and
 * any other role opens the sample-data portal: a null web home may only mean the API could not read
 * Fabrica, so it never sends the person to the mobile app alone. Any other portals its menus reach
 * are mounted too.
 */
export function webRolesOf(access: UserAccess, menus: FabricaMenus | null): WebRole[] {
  return access.roles.flatMap((r): WebRole[] => {
    const paths = menus ? menuPaths(menus[r.roleId] ?? []) : null;
    const menuHome = paths?.find((p) => portalOfPath(p));
    if (!r.webHome && paths && !menuHome) return [];
    let portal: Portal | null;
    let home: string;
    if (r.webHome) {
      portal = portalOfShell(r.webHome.shell) ?? portalOfPath(r.webHome.path);
      home = r.webHome.path;
    } else if (menuHome) {
      home = menuHome;
      portal = portalOfPath(home);
    } else {
      portal = getDefaultRole();
      home = PORTAL_HOME_PATH[portal];
    }
    if (!portal) return [];
    const portals = [portal, ...portalsOfPaths(paths ?? []).filter((p) => p !== portal)];
    return [
      {
        roleId: r.roleId,
        code: r.code,
        name: r.name,
        icon: r.icon ?? null,
        portal,
        portals,
        home,
        operations: r.operations ?? null,
      },
    ];
  });
}

/** True when `operations` (a role's; null or undefined when not known) grant `key`, ignoring case. Not known: false. */
export function grantsOperation(
  operations: readonly string[] | null | undefined,
  key: string,
): boolean {
  return !!operations?.some((o) => o.toLowerCase() === key.toLowerCase());
}

/** The Fabrica operation that opens the Fabrica admin portal (the API's RequireFabricaAdmin). */
export const FABRICA_ADMIN_OPERATION = 'Fabrica.Admin';

/**
 * May open the Fabrica admin portal: one of the person's roles is granted Fabrica.Admin, a platform
 * role (operations from ValidateActiveUser) or a school role (operations from /api/me). The API
 * checks every call again (RequireFabricaAdmin).
 */
export const mayOpenFabricaAdmin = (access: UserAccess | null | undefined) =>
  !!access &&
  [...(access.platformRoles ?? []), ...access.roles].some((r) =>
    grantsOperation(r.operations, FABRICA_ADMIN_OPERATION),
  );

export const displayName = (access: UserAccess) =>
  `${access.user.firstName} ${access.user.lastName}`.trim();
